import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { StringParam, useQueryParam, withDefault } from 'use-query-params';
import { useDashboardFeatureFlags } from '@components';
import { useCurrentShop } from '@modules/Dashboard/hooks/useCurrentShop';
import { useMembershipAccountQuery } from '@modules/Dashboard/hooks/useMembershipAccountQuery';
import { useReportType } from '../../../hooks/useReportType';
import { createAnalyticsFilters } from '../AnalyticsFilters.consts';
import { isAnyFilterActive } from '../utils/isAnyFilterActive';
import { isFilterActive } from '../utils/isFilterActive';
import { mapFiltersToUrlParams } from '../utils/mapFiltersToUrlParams';
import { mapUrlParamsToFilters } from '../utils/mapUrlParamsToFilters';
const AnalyticsFiltersContext = createContext(undefined);
export const AnalyticsFiltersProvider = ({ children }) => {
    const { reportType } = useReportType();
    const { dashboardFeatureFlags } = useDashboardFeatureFlags();
    const { currentShop } = useCurrentShop({ staleTime: Infinity });
    const membershipsEnabled = !!currentShop?.settings.memberships?.enabled && dashboardFeatureFlags.memberships.enabled;
    const { membershipAccountQuery } = useMembershipAccountQuery({
        enabled: membershipsEnabled,
    });
    // URL params for multiparams - we use StringParam with comma-separated values
    const [chartEntityParams, setChartEntityParams] = useQueryParam('chartEntity', withDefault(StringParam, ''));
    const [tiersIdsParams, setTiersIdsParams] = useQueryParam('tiersIds', withDefault(StringParam, ''));
    const [intervalsParams, setIntervalsParams] = useQueryParam('intervals', withDefault(StringParam, ''));
    const [devicesParams, setDevicesParams] = useQueryParam('devices', withDefault(StringParam, ''));
    const tiers = useMemo(() => membershipAccountQuery?.data?.tiers.map(({ id, name }) => {
        return { label: name, value: id };
    }) || [], [membershipAccountQuery?.data?.tiers]);
    const initialFilters = useMemo(() => createAnalyticsFilters({
        membershipsEnabled,
        membershipsWlaEnabled: dashboardFeatureFlags.membershipsWla.enabled,
        tiers,
    })[reportType] ?? [], [membershipsEnabled, reportType, tiers, dashboardFeatureFlags.membershipsWla.enabled]);
    // Initialize filters from URL params
    const [filters, setFilters] = useState(() => mapUrlParamsToFilters(initialFilters, chartEntityParams, tiersIdsParams, intervalsParams, devicesParams));
    // Update filters when URL params change (only one-way sync from URL to filters)
    useEffect(() => {
        setFilters(mapUrlParamsToFilters(initialFilters, chartEntityParams, tiersIdsParams, intervalsParams, devicesParams));
    }, [chartEntityParams, tiersIdsParams, intervalsParams, devicesParams, initialFilters]);
    const handleFiltersChange = useCallback((sectionTitle, itemValue, checked) => {
        setFilters((prevFilters) => {
            const newFilters = prevFilters.map((section) => {
                if (section.title !== sectionTitle) {
                    return section;
                }
                const itemIndex = section.items.findIndex((item) => {
                    if (Array.isArray(item.value)) {
                        return item.value.includes(itemValue);
                    }
                    return item.value === itemValue;
                });
                if (itemIndex === -1) {
                    return section;
                }
                const updatedItems = [...section.items];
                const item = updatedItems[itemIndex];
                updatedItems[itemIndex] = { ...item, checked };
                return {
                    ...section,
                    items: updatedItems,
                };
            });
            // Update URL params based on new filters
            const paramGroups = mapFiltersToUrlParams(newFilters);
            setChartEntityParams(paramGroups.chartEntity?.join(',') || undefined);
            setTiersIdsParams(paramGroups.tiersIds?.join(',') || undefined);
            setIntervalsParams(paramGroups.intervals?.join(',') || undefined);
            setDevicesParams(paramGroups.devices?.join(',') || undefined);
            return newFilters;
        });
    }, [setChartEntityParams, setTiersIdsParams, setIntervalsParams, setDevicesParams]);
    const hasActiveFilters = useMemo(() => isAnyFilterActive(filters, initialFilters), [filters, initialFilters]);
    // Function to check if a specific filter is active
    const checkFilterActive = useCallback((filterType, value) => {
        return isFilterActive(filters, filterType, value);
    }, [filters]);
    // Function to clear all filters
    const clearFilters = useCallback(() => {
        setFilters(initialFilters);
        // Clear URL params
        setChartEntityParams(undefined);
        setTiersIdsParams(undefined);
        setIntervalsParams(undefined);
        setDevicesParams(undefined);
    }, [
        initialFilters,
        setChartEntityParams,
        setTiersIdsParams,
        setIntervalsParams,
        setDevicesParams,
    ]);
    const value = useMemo(() => ({
        filters,
        hasActiveFilters,
        checkFilterActive,
        onChange: handleFiltersChange,
        clearFilters,
    }), [filters, hasActiveFilters, checkFilterActive, handleFiltersChange, clearFilters]);
    return (React.createElement(AnalyticsFiltersContext.Provider, { value: value }, children));
};
export const useAnalyticsFilters = () => {
    const context = useContext(AnalyticsFiltersContext);
    if (!context) {
        throw new Error('useAnalyticsFilters must be used within AnalyticsFiltersProvider');
    }
    return context;
};
